import { Box, Popover, type PopoverProps } from "@mui/material";
import { type ReactNode, type RefObject, useCallback, useRef, useState } from "react";

export interface HoverPopoverProps {
  /** The trigger element that shows the popover on hover */
  children: ReactNode;
  /** The content to display inside the popover */
  content: ReactNode;
  /** Optional external ref for the anchor element */
  anchorRef?: RefObject<HTMLElement | null>;
  /** Delay in ms before showing the popover (default: 200) */
  openDelay?: number;
  /** Delay in ms before hiding the popover (default: 100) */
  closeDelay?: number;
  /** Popover placement relative to anchor */
  anchorOrigin?: PopoverProps["anchorOrigin"];
  /** Popover transform origin */
  transformOrigin?: PopoverProps["transformOrigin"];
  /** Max width of the popover content */
  maxWidth?: number | string;
  /** Max height of the popover content */
  maxHeight?: number | string;
  /** Additional props to pass to the Popover */
  PopoverProps?: Partial<PopoverProps>;
  /** Disable the popover */
  disabled?: boolean;
}

export function HoverPopover({
  children,
  content,
  anchorRef: externalAnchorRef,
  openDelay = 200,
  closeDelay = 100,
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  transformOrigin = { vertical: "top", horizontal: "left" },
  maxWidth = 400,
  maxHeight = 300,
  PopoverProps: popoverProps,
  disabled = false,
}: HoverPopoverProps) {
  const [open, setOpen] = useState(false);
  const internalAnchorRef = useRef<HTMLDivElement>(null);
  const anchorRef = externalAnchorRef || internalAnchorRef;
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeouts = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    clearTimeouts();
    openTimeoutRef.current = setTimeout(() => {
      setOpen(true);
    }, openDelay);
  }, [openDelay, clearTimeouts, disabled]);

  const handleMouseLeave = useCallback(() => {
    clearTimeouts();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, closeDelay);
  }, [closeDelay, clearTimeouts]);

  // Keep popover open when hovering over it
  const handlePopoverMouseEnter = useCallback(() => {
    clearTimeouts();
  }, [clearTimeouts]);

  const handlePopoverMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, closeDelay);
  }, [closeDelay]);

  return (
    <>
      <Box
        ref={externalAnchorRef ? undefined : internalAnchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: "inline-flex" }}
      >
        {children}
      </Box>
      <Popover
        open={open && !disabled}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
        sx={{
          pointerEvents: "none",
          ...popoverProps?.sx,
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
            sx: {
              pointerEvents: "auto",
              maxWidth,
              maxHeight,
              overflow: "auto",
              boxShadow: 3,
              ...popoverProps?.slotProps?.paper,
            },
          },
        }}
        {...popoverProps}
      >
        <Box sx={{ p: 1.5 }}>{content}</Box>
      </Popover>
    </>
  );
}

export default HoverPopover;
